import React, { useCallback } from 'react';

import { ErrorMessage } from './ErrorMessage';
import Input from './Input';

interface HourMinute {
  hour: number;
  minute: number;
}

const minutesToHourMinute = (timeInMinutes: number): HourMinute => {
  const hour = Math.floor(timeInMinutes / 60);
  const minute = timeInMinutes - hour * 60;
  return { hour, minute };
};

const hourMinuteToMinutes = ({ hour, minute }: HourMinute): number =>
  hour * 60 + minute;

interface HourMinuteInputProps {
  className?: string;
  valueInMinutes: number;
  max?: number;
  onChange: (valueInMinutes: number) => void;
  error?: Possible<string>;
}

const HourMinuteInput: React.FC<HourMinuteInputProps> = ({
  className,
  valueInMinutes,
  max,
  onChange: onChangeProp,
  error,
}) => {
  const { hour, minute } = minutesToHourMinute(valueInMinutes);

  const onChange = useCallback(
    (changedValues: HourMinute) => {
      const timeInMinutes = hourMinuteToMinutes({
        hour: changedValues.hour,
        minute: Math.min(changedValues.minute, 59),
      });
      if (max != null && timeInMinutes > max) {
        return;
      }

      onChangeProp(timeInMinutes);
    },
    [max, onChangeProp],
  );

  return (
    <div className={className}>
      <div className="flex w-full flex-col items-center space-y-3 lg:flex-row lg:space-x-3 lg:space-y-0">
        <Input
          className="w-full"
          label="hours"
          labelIsHidden
          placeholder="hours..."
          postfix="hours"
          type="number"
          value={hour.toString()}
          min={0}
          onChange={(e) =>
            onChange({
              hour: parseInt(e.currentTarget.value) || 0,
              minute,
            })
          }
        />
        <Input
          className="w-full"
          label="minutes"
          labelIsHidden
          placeholder="minutes..."
          type="number"
          postfix="minutes"
          value={minute.toString()}
          min={0}
          max={59}
          onChange={(e) =>
            onChange({
              hour,
              minute: parseInt(e.currentTarget.value) || 0,
            })
          }
        />
      </div>
      {error ? <ErrorMessage errorMessage={error} /> : null}
    </div>
  );
};

export default HourMinuteInput;
